import { useCallback, useEffect, useRef, useState } from 'react';
import useInboxStore from '@/stores/InboxStore'; // Import InboxStore
import useWorkflowStore from '@/stores/WorkflowStore';
import {
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from '@xyflow/react';
import * as d3Hierarchy from 'd3-hierarchy';
import { v4 as uuidv4 } from 'uuid';
import { useShallow } from 'zustand/react/shallow';

// Reuse custom elements and styles
import CustomEdge from '../CustomEdge';
import {
  ActionNode,
  BranchConfigNode,
  BranchDataNode,
  EndNode,
  TriggerNode,
} from '../CustomNodes';

import '@xyflow/react/dist/style.css';

import { toast } from 'sonner';

import CustomControls from '../CustomControls';
// Import configuration components from the edit directory
import ActionConfiguration from './ActionConfiguration';
import TriggerConfiguration from './TriggerConfiguration';

const nodeTypes = {
  trigger: (props) => <TriggerNode {...props} />,
  end: (props) => <EndNode {...props} />,
  action: (props) => <ActionNode {...props} />,
  'branch-config': (props) => <BranchConfigNode {...props} />,
  'branch-data': (props) => <BranchDataNode {...props} />,
};

const edgeTypes = {
  custom: CustomEdge,
};

// Utility to calculate layout using d3-hierarchy
function calculateLayout(nodes, edges, options = {}) {
  const { direction = 'TB', nodeWidth = 180, nodeHeight = 60 } = options;

  if (!nodes || nodes.length === 0) return [];

  // Helper function to build a hierarchical structure from graph
  const buildHierarchy = () => {
    // Find the root node (usually the trigger)
    const rootNode = nodes.find((n) => n.type === 'trigger');
    if (!rootNode) return null;

    // Create a map of node id to its children
    const childrenMap = {};
    edges.forEach((edge) => {
      if (!childrenMap[edge.source]) {
        childrenMap[edge.source] = [];
      }
      childrenMap[edge.source].push(edge.target);
    });

    // Build the hierarchy recursively
    const buildNode = (nodeId) => {
      const node = nodes.find((n) => n.id === nodeId);
      if (!node) return null;

      // Get children for this node
      const childIds = childrenMap[nodeId] || [];
      const children = childIds.map((id) => buildNode(id)).filter(Boolean);

      // Return hierarchical node
      return {
        id: nodeId,
        type: node.type,
        data: node.data,
        width: node.measured?.width || node.width || nodeWidth,
        height: node.measured?.height || node.height || nodeHeight,
        originalNode: node,
        children: children,
      };
    };

    return buildNode(rootNode.id);
  };

  // Build the hierarchy
  const hierarchyRoot = buildHierarchy();
  if (!hierarchyRoot) return nodes; // Return original nodes if couldn't build hierarchy

  // Create hierarchy tree
  const root = d3Hierarchy.hierarchy(hierarchyRoot);

  // Create tree layout based on direction
  const treeLayout = d3Hierarchy
    .tree()
    .nodeSize([
      direction === 'TB' ? nodeWidth * 1.5 : nodeHeight * 2,
      direction === 'TB' ? nodeHeight * 4.75 : nodeWidth * 1.5,
    ])
    .separation((a, b) => {
      // Special handling for branch-data nodes to preserve their order
      if (
        a.parent === b.parent &&
        (a.data.type === 'branch-data' || b.data.type === 'branch-data')
      ) {
        return 1.5;
      }

      // Add extra spacing if parent is a trigger node
      if (a.parent && a.parent.data.type === 'trigger') {
        return 1.5; // Increased separation for trigger node children
      }

      return a.parent === b.parent ? 1 : 2;
    });

  // Apply the layout
  treeLayout(root);

  // Create a map for quick lookup
  const positionMap = {};
  root.descendants().forEach((node) => {
    positionMap[node.data.id] = {
      x: direction === 'TB' ? node.x : node.y,
      y: direction === 'TB' ? node.y : node.x,
    };
  });

  // Apply calculated positions to original nodes
  const laidOutNodes = nodes.map((node) => {
    const position = positionMap[node.id];
    if (!position) {
      return node; // Keep original position if not in the hierarchy
    }

    // Center the node on the calculated position
    const width = node.measured?.width || node.width || nodeWidth;
    const height = node.measured?.height || node.height || nodeHeight;

    return {
      ...node,
      position: {
        x: position.x - width / 2,
        y: position.y - height / 2,
      },
      width,
      height,
    };
  });

  return laidOutNodes;
}

// Helper function to find all downstream nodes and edges from a starting node ID (from ActionConfiguration)
const findAllDownstreamElements = (startNodeId, allNodes, allEdges) => {
  const nodesToDelete = new Set();
  const edgesToDelete = new Set();
  const queue = [startNodeId];
  const visited = new Set([startNodeId]); // Track visited nodes within this traversal

  // Add the starting node itself for deletion
  nodesToDelete.add(startNodeId);

  let head = 0;
  while (head < queue.length) {
    const currentNodeId = queue[head++];

    // Find outgoing edges from the current node
    const outgoingEdges = allEdges.filter((e) => e.source === currentNodeId);

    for (const edge of outgoingEdges) {
      edgesToDelete.add(edge.id); // Mark the edge for deletion
      const targetNodeId = edge.target;

      // Find the target node
      const targetNode = allNodes.find((n) => n.id === targetNodeId);

      // If target node exists and hasn't been visited in this traversal
      if (targetNode && !visited.has(targetNodeId)) {
        visited.add(targetNodeId);
        nodesToDelete.add(targetNodeId); // Mark the target node for deletion
        queue.push(targetNodeId); // Add target node to the queue for further traversal
      } else if (targetNode && visited.has(targetNodeId)) {
        // If we encounter a node already visited (potential cycle or merge point),
        // still delete the incoming edge, but don't re-process the node.
      }
    }
  }

  return { nodesToDelete, edgesToDelete };
};

const Flow = () => {
  const { tempNodes, tempEdges, setTempNodes, setTempEdges } = useWorkflowStore(
    useShallow((state) => ({
      tempNodes: state.tempNodes,
      tempEdges: state.tempEdges,
      setTempNodes: state.setTempNodes,
      setTempEdges: state.setTempEdges,
    }))
  );

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const { fitView } = useReactFlow();

  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);
  const processedRef = useRef(false);
  const isFirstLayoutRef = useRef(true);
  const [isLoading, setIsLoading] = useState(true);

  const { owners, tags } = useInboxStore(
    useShallow((state) => ({
      owners: state.owners,
      tags:
        state.tags.filter(
          (tag) =>
            // Filter out special system tags that aren't actual tags
            !['No-Tag-SBCCRM', 'Seen-Not-Responded', 'Not-Responded'].includes(
              tag._id
            )
        ) || [],
    }))
  );

  // --- Define Deletion Handlers --- Needed for Action/Branch Nodes
  const handleActionDelete = useCallback(
    (nodeIdToDelete) => {
      setEdges((eds) => {
        const incomingEdge = eds.find((edge) => edge.target === nodeIdToDelete);
        const outgoingEdge = eds.find((edge) => edge.source === nodeIdToDelete);

        const filteredEdges = eds.filter(
          (edge) =>
            edge.target !== nodeIdToDelete && edge.source !== nodeIdToDelete
        );

        if (incomingEdge && outgoingEdge) {
          const newEdge = {
            ...outgoingEdge, // Keep properties like type, data etc.
            id: `e-${incomingEdge.source}-${
              outgoingEdge.target
            }-${uuidv4().substring(0, 4)}`,
            source: incomingEdge.source,
            target: outgoingEdge.target,
            // Ensure correct data is passed for the new edge
            data: {
              ...(outgoingEdge.data || {}),
              showAddButton: true,
              // Decide if allowBranchStep is true based on the new source node type
              allowBranchStep:
                nodesRef.current.find((n) => n.id === incomingEdge.source)
                  ?.type !== 'branch-config', // Example logic
              setIsActionTypeModalOpen,
              setActionSourceNode,
            },
          };
          return [...filteredEdges, newEdge];
        }
        return filteredEdges;
      });

      setNodes((nds) => nds.filter((node) => node.id !== nodeIdToDelete));
      // Layout will be triggered by state update effect
    },
    [setNodes, setEdges] // Removed runLayout dependency
  );

  const handleDeleteBranchConfig = useCallback(
    (nodeIdToDelete) => {
      const currentNodes = nodesRef.current; // Use ref for latest state
      const currentEdges = edgesRef.current; // Use ref for latest state

      let nodesToDelete = new Set();
      let edgesToDelete = new Set();
      let sourceId = null;

      const nodesToAdd = [];
      const edgesToAdd = [];

      nodesToDelete.add(nodeIdToDelete);

      const incomingEdge = currentEdges.find(
        (e) => e.target === nodeIdToDelete
      );
      if (incomingEdge) {
        edgesToDelete.add(incomingEdge.id);
        sourceId = incomingEdge.source;
      }

      const directOutgoingEdges = currentEdges.filter(
        (e) => e.source === nodeIdToDelete
      );

      // Track targets of deleted branches to potentially add End nodes
      const targetsNeedingEnd = new Set();

      directOutgoingEdges.forEach((edge) => {
        edgesToDelete.add(edge.id); // Edge: config -> data
        const branchDataNodeId = edge.target;

        // Find all downstream elements from this branch data node
        const {
          nodesToDelete: downstreamNodes,
          edgesToDelete: downstreamEdges,
        } = findAllDownstreamElements(
          branchDataNodeId,
          currentNodes,
          currentEdges
        );

        downstreamNodes.forEach((id) => nodesToDelete.add(id));
        downstreamEdges.forEach((id) => edgesToDelete.add(id));

        // Mark the source as needing an end node if it exists
        if (sourceId) {
          targetsNeedingEnd.add(sourceId);
        }
      });

      // Add a single new End node for the original source if needed
      if (targetsNeedingEnd.has(sourceId)) {
        const sourceNode = currentNodes.find((n) => n.id === sourceId);
        if (sourceNode) {
          const newEndNodeId = `end-${sourceId}-${uuidv4().substring(0, 4)}`;
          const newEndNode = {
            id: newEndNodeId,
            type: 'end',
            position: {
              x: sourceNode.position.x,
              y: sourceNode.position.y + 250,
            }, // Placeholder position
            data: { label: 'END' },
          };
          nodesToAdd.push(newEndNode);
          edgesToAdd.push({
            id: `e-${sourceId}-${newEndNodeId}`,
            source: sourceId,
            target: newEndNodeId,
            type: 'custom',
            className: 'stroke-slate-300 stroke-[1.5px]',
            data: {
              showAddButton: true,
              allowBranchStep: true, // Allow adding before this new end
              setIsActionTypeModalOpen,
              setActionSourceNode,
            },
          });
        }
      }

      // Update state using functional updates
      setNodes((nds) => [
        ...nds.filter((n) => !nodesToDelete.has(n.id)),
        ...nodesToAdd,
      ]);
      setEdges((eds) => [
        ...eds.filter((e) => !edgesToDelete.has(e.id)),
        ...edgesToAdd,
      ]);

      // Layout will be triggered by state update effect
    },
    [setNodes, setEdges] // Removed runLayout dependency
  );

  // --- Layout Function ---
  const runLayout = useCallback(() => {
    setTimeout(() => {
      const currentNodes = nodesRef.current;
      const currentEdges = edgesRef.current;
      const hasNodes = currentNodes.length > 0;
      const allNodesMeasured =
        hasNodes && currentNodes.every((n) => n.measured?.width);

      if (hasNodes && allNodesMeasured) {
        const laidOutNodes = calculateLayout(currentNodes, currentEdges);

        const positionsDiffer =
          JSON.stringify(currentNodes.map((n) => n.position)) !==
          JSON.stringify(laidOutNodes.map((n) => n.position));

        if (positionsDiffer) {
          // Use functional update to avoid stale state issues
          setNodes((prevNodes) => {
            const nodeMap = new Map(prevNodes.map((n) => [n.id, n]));
            return laidOutNodes.map((layoutNode) => ({
              ...nodeMap.get(layoutNode.id), // Keep existing node data
              ...layoutNode, // Apply new position, width, height from layout
            }));
          });
          setTimeout(() => {
            fitView({ padding: 0.1, duration: 200 });
          }, 10);
        } else {
          console.log('Skipping layout update (edit, positions same).');
        }
      } else if (hasNodes) {
        console.log('Layout skipped (edit): Nodes not measured.');
      } else {
        console.log('Layout skipped (edit): No nodes.');
      }
    }, 0);
  }, [setNodes, fitView]); // Dependencies: setNodes, fitView

  // --- Initial Load and Handler Injection --- Needed for Edit Mode
  useEffect(() => {
    // Only run this effect once when tempNodes and tempEdges are first available
    if (!tempNodes?.length || !tempEdges?.length) return;

    if (processedRef.current) return;
    processedRef.current = true;

    // Inject handlers into nodes loaded from the store
    const nodesWithHandlers = tempNodes.map((node) => {
      const data = { ...node.data };

      // Inject deletion handlers and populate related data (like tagObjects/ownerObject)
      if (node.type === 'action') {
        data.handleActionDelete = handleActionDelete;

        // Populate tagObjects for tag-related actions
        if (
          (node.data.actionType === 'contact_add_tag' ||
            node.data.actionType === 'contact_remove_tag') &&
          node.data.config?.tags &&
          !node.data.config.tagObjects // Only add if not already present
        ) {
          const tagIds = node.data.config.tags || [];
          const tagObjects = tagIds
            .map((tagId) => {
              const tagObj = tags.find((t) => t._id === tagId);
              return tagObj
                ? { id: tagId, name: tagObj.name, colour: tagObj.colour }
                : null;
            })
            .filter(Boolean);
          data.config.tagObjects = tagObjects;
        }

        // Populate ownerObject for change owner action (single mode)
        if (
          node.data.actionType === 'contact_change_owner' &&
          node.data.config?.ownerMode !== 'multiple' &&
          node.data.config?.owner &&
          !node.data.config.ownerObject // Only add if not already present
        ) {
          const ownerId = node.data.config.owner;
          const ownerObj = owners.find((o) => o._id === ownerId);
          if (ownerObj) {
            data.config.ownerObject = {
              id: ownerObj._id,
              name: `${ownerObj.first_name} ${ownerObj.last_name}`,
            };
          }
        }

        // Populate ownersWithDetails for change owner action (multiple mode)
        if (
          node.data.actionType === 'contact_change_owner' &&
          node.data.config?.ownerMode === 'multiple' &&
          node.data.config?.owners &&
          !node.data.config.ownersWithDetails // Only add if not already present
        ) {
          const ownersWithDetails = node.data.config.owners.map((alloc) => {
            const ownerObj = owners.find((o) => o._id === alloc.user_id);
            return {
              ...alloc,
              ownerObject: ownerObj
                ? {
                    id: ownerObj._id,
                    name: `${ownerObj.first_name} ${ownerObj.last_name}`,
                  }
                : null,
            };
          });
          data.config.ownersWithDetails = ownersWithDetails;
        }
      }
      if (node.type === 'branch-config') {
        data.handleDeleteBranchConfig = handleDeleteBranchConfig;
      }
      // --- Populate tagObjects for contact_tag triggers --- // Add this block
      if (
        node.type === 'trigger' &&
        node.data.triggerType === 'contact_tag' &&
        node.data.config?.tags &&
        !node.data.config.tagObjects // Only add if not already present
      ) {
        const tagIds = node.data.config.tags || [];
        const tagObjects = tagIds
          .map((tagId) => {
            const tagObj = tags.find((t) => t._id === tagId);
            return tagObj
              ? { id: tagId, name: tagObj.name, colour: tagObj.colour }
              : null;
          })
          .filter(Boolean);
        data.config.tagObjects = tagObjects;
      }
      // --- End tagObjects population for triggers ---
      if (
        node.type === 'trigger' &&
        node.data.triggerType === 'schedule' &&
        node.data.config.conditions
      ) {
        data.config.conditions = node.data.config.conditions.map(
          (condition) => {
            if (condition.field === 'contact_tag') {
              const tagObjects = condition.value
                .map((tagId) => {
                  const tagObj = tags.find((t) => t._id === tagId);
                  return tagObj
                    ? { id: tagId, name: tagObj.name, colour: tagObj.colour }
                    : null;
                })
                .filter(Boolean);
              return {
                ...condition,
                tagObjects,
              };
            }
            return condition;
          }
        );
      }
      // Add other handlers if needed
      return { ...node, data };
    });

    const edgesWithHandlers = tempEdges.map((edge) => {
      const data = {
        ...(edge.data || {}),
        setIsActionTypeModalOpen,
        setActionSourceNode,
      };

      return { ...edge, data };
    });

    setNodes(nodesWithHandlers);
    setEdges(edgesWithHandlers);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tempNodes, tempEdges]); // Dependencies needed to detect when data is available

  useEffect(() => {
    nodesRef.current = nodes;
    edgesRef.current = edges;

    setTempNodes(nodes);
    setTempEdges(edges);

    if (isFirstLayoutRef.current && nodes.length > 0) {
      setTimeout(() => {
        runLayout();
        isFirstLayoutRef.current = false;
        setIsLoading(false);
      }, 100);
    } else {
      setTimeout(runLayout, 100);
    }
  }, [nodes, edges, runLayout]);

  // --- State for Modals/Sheets ---
  const [isTriggerModalOpen, setIsTriggerModalOpen] = useState(false);
  const [isConfigSheetOpen, setIsConfigSheetOpen] = useState(false);
  const [selectedTriggerNode, setSelectedTriggerNode] = useState(null);

  const [isActionTypeModalOpen, setIsActionTypeModalOpen] = useState(false);
  const [isActionConfigSheetOpen, setIsActionConfigSheetOpen] = useState(false);
  const [selectedActionNode, setSelectedActionNode] = useState(null);
  const [actionSourceNode, setActionSourceNode] = useState(null); // Needed by CustomEdge/Config components

  const [isBranchConfigOpen, setIsBranchConfigOpen] = useState(false);

  // --- Utility function to find the parent branch-config node ---
  const findParentBranchConfigNode = (branchDataNodeId) => {
    const incomingEdges = edgesRef.current.filter(
      (edge) => edge.target === branchDataNodeId
    );

    for (const edge of incomingEdges) {
      const sourceNode = nodesRef.current.find(
        (node) => node.id === edge.source
      );

      if (sourceNode && sourceNode.type === 'branch-config') {
        return sourceNode;
      }
    }

    toast.error('No branch configuration found');
    return null;
  };

  // --- Node Click Handlers ---
  const handleTriggerNodeClick = useCallback((node) => {
    setSelectedTriggerNode(node);
    if (node.data?.triggerType) {
      setIsConfigSheetOpen(true);
    } else {
      setIsTriggerModalOpen(true);
    }
  }, []);

  const handleActionNodeClick = useCallback((node) => {
    setSelectedActionNode(node);
    if (node.type === 'branch-config') {
      setIsBranchConfigOpen(true);
    } else if (node.type === 'action') {
      setIsActionConfigSheetOpen(true);
    }
  }, []);

  const onNodeClick = useCallback(
    (event, node) => {
      if (node.type === 'trigger') {
        handleTriggerNodeClick(node);
      } else if (node.type === 'action' || node.type === 'branch-config') {
        handleActionNodeClick(node);
      } else if (node.type === 'branch-data') {
        const parentConfigNode = findParentBranchConfigNode(node.id);
        if (parentConfigNode) {
          handleActionNodeClick(parentConfigNode);
        }
      }
      // Add handlers for other node types if needed
    },
    [handleTriggerNodeClick, handleActionNodeClick]
  );

  return (
    <>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodeClick={onNodeClick}
        nodesDraggable={false} // Keep nodes non-draggable initially
        fitView
        proOptions={{ hideAttribution: true }}
      >
        <CustomControls />
      </ReactFlow>

      {/* Loading Overlay */}
      {isLoading && (
        <div className='absolute inset-0 z-50 flex items-center justify-center bg-background/100'>
          <div className='flex flex-col items-center gap-2'>
            <div className='h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent'></div>
            <p className='text-sm text-muted-foreground'>
              Preparing workflow...
            </p>
          </div>
        </div>
      )}

      {/* Configuration Components */}
      <TriggerConfiguration
        // Pass state needed for the trigger config component
        isTriggerModalOpen={isTriggerModalOpen}
        setIsTriggerModalOpen={setIsTriggerModalOpen}
        isConfigSheetOpen={isConfigSheetOpen}
        setIsConfigSheetOpen={setIsConfigSheetOpen}
        selectedTriggerNode={selectedTriggerNode}
        setSelectedTriggerNode={setSelectedTriggerNode}
        setIsActionTypeModalOpen={setIsActionTypeModalOpen}
        setActionSourceNode={setActionSourceNode}
        nodes={nodes} // Pass internal nodes
        setNodes={setNodes} // Pass internal setter
        edges={edges} // Pass internal edges
        setEdges={setEdges} // Pass internal setter
        runLayout={runLayout} // Pass layout function
      />

      <ActionConfiguration
        // Pass state needed for the action config component
        isActionTypeModalOpen={isActionTypeModalOpen}
        setIsActionTypeModalOpen={setIsActionTypeModalOpen}
        isActionConfigSheetOpen={isActionConfigSheetOpen}
        setIsActionConfigSheetOpen={setIsActionConfigSheetOpen}
        selectedActionNode={selectedActionNode}
        setSelectedActionNode={setSelectedActionNode}
        actionSourceNode={actionSourceNode}
        setActionSourceNode={setActionSourceNode}
        isBranchConfigOpen={isBranchConfigOpen}
        setIsBranchConfigOpen={setIsBranchConfigOpen}
        // Pass setters for nodes/edges for updates
        nodes={nodes} // Pass internal nodes
        setNodes={setNodes} // Pass internal setter
        edges={edges} // Pass internal edges
        setEdges={setEdges} // Pass internal setter
        runLayout={runLayout} // Pass layout function
        // Pass deletion handlers needed by ActionConfiguration when adding nodes
        handleActionDelete={handleActionDelete}
        handleDeleteBranchConfig={handleDeleteBranchConfig}
      />
    </>
  );
};

const WorkflowEditor = () => {
  // Check if tempNodes have loaded to prevent rendering Flow prematurely
  //   const hasLoaded = useWorkflowStore((state) => state.tempNodes.length > 0);

  //   if (!hasLoaded) {
  //     // Optional: Add a more specific loading indicator for the editor itself
  //     return null; // Or a loading spinner
  //   }

  return (
    <div className='relative h-full w-full bg-background'>
      <ReactFlowProvider>
        <Flow />
      </ReactFlowProvider>
    </div>
  );
};

export default WorkflowEditor;
